import React from "react"
import { DATE_PRESET_OPTIONS, DateRange, NO_DATE_OPTION } from "../utils/datePreset"
import { Chip, Group, Skeleton, Text } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { CalendarBlankIcon } from "@phosphor-icons/react"
import "dayjs/locale/ja"

interface Props {
  value: string
  dateRange: DateRange
  onChangeValue: (value: string) => void
  onChangeDateRange: (dateRange: DateRange) => void

  /** 紹介者一覧 / ゲスト一覧のみ先頭に「指定なし」を出す */
  withNoneOption?: boolean

  /** 期間プリセット行のラベル。CRMスコアリング分析のみ「最終紹介日」を渡す */
  presetLabel?: string

  /** 未来日の選択を禁止する画面のみ当日を渡す */
  maxDate?: string

  /** 過去日の選択を禁止する画面のみ下限日を渡す */
  minDate?: string

  /** 表示しないプリセットの値。期間上限を超えるものを除くために使う */
  hiddenPresets?: string[]

  /** true の間はチップ行をスケルトンにする。期間上限の取得中に使う */
  loading?: boolean
}

// 「期間」プリセット行と「開始日～終了日」行の2行。CV一覧 / 紹介者一覧 / ゲスト一覧で共用する
// 外枠の Box は呼び出し側が持つため Fragment で2つの Group を返す
export const DatePresetFilter = ({
  value,
  dateRange,
  onChangeValue,
  onChangeDateRange,
  withNoneOption = false,
  presetLabel = "期間",
  maxDate,
  minDate,
  hiddenPresets = [],
  loading = false,
}: Props) => {
  const baseOptions = withNoneOption
    ? [NO_DATE_OPTION, ...DATE_PRESET_OPTIONS]
    : [...DATE_PRESET_OPTIONS]
  const options = baseOptions.filter(({ value: optionValue }) => !hiddenPresets.includes(optionValue))

  const handleChangeValue = (next: string) => {
    onChangeValue(next)

    // custom 以外では日付欄を使わないため、入力済みの値をクリアして送信値の残留を防ぐ
    if (next !== 'custom') {
      onChangeDateRange([null, null])
    }
  }

  return (
    <>
      <Group gap={24}>
        <Text fz={13} fw={600} c="#000000CC" style={{ width: "100px", textAlign: "start" }}>{presetLabel}</Text>
        {loading ? (
          <Group gap={8}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} h={28} w={72} radius="xl" />
            ))}
          </Group>
        ) : (
          <Chip.Group
            multiple={false}
            value={value}
            onChange={(v) => handleChangeValue((v as string) ?? '')}
          >
            <Group justify="center" gap={8}>
              {options.map(({ value: optionValue, label }) => (
                <Chip
                  key={optionValue}
                  value={optionValue}
                  variant="outline"
                  className="invy-chip"
                >
                  {label}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        )}
      </Group>

      <Group gap={24} mt={16}>
        <Text fz={13} fw={600} c="#000000CC" style={{ width: "100px", textAlign: "start" }}>開始日～終了日</Text>
        <DatePickerInput
          type="range"
          valueFormat="YYYY.MM.DD"
          w={250}
          rightSection={
            <CalendarBlankIcon
              size={16}
              weight="fill"
              color={value === "custom" ? "#00000099" : "#ADB5BD"}
            />
          }
          rightSectionPointerEvents="none"
          value={dateRange}
          onChange={(v) => onChangeDateRange([v[0] ?? null, v[1] ?? null])}
          placeholder="YYYY.MM.DD - YYYY.MM.DD"
          disabled={value !== "custom"}
          locale="ja"
          maxDate={maxDate}
          minDate={minDate}
          allowSingleDateInRange
        />
      </Group>
    </>
  )
}
